export type LoginMode = 'account' | 'oidc' | 'saml'

export const SIDEBAR_MENU_KEYS = [
  'dashboard',
  'agents',
  'applications',
  'channels',
  'channel-accounts',
  'credentials',
  'sandboxes',
  'mcps',
  'memories',
  'models',
  'rags-build',
  'rags-catalog',
  'skillgroups',
  'skills-management',
  'skills-plaza',
  'iam-users',
  'iam-groups',
  'iam-policies',
  'iam-grants',
  'webchat'
] as const

export type SidebarMenuKey = (typeof SIDEBAR_MENU_KEYS)[number]

export const DISABLED_ROUTE_PATTERNS = [
  '/agents/*',
  '/agent-templates',
  '/applications/*',
  '/channels/*',
  '/channel-accounts/*',
  '/channel-sessions/*',
  '/credentials',
  '/plugins/sandboxes/*',
  '/plugins/mcps/*',
  '/plugins/memories/*',
  '/plugins/models',
  '/plugins/rags/*',
  '/plugins/skills/*',
  '/iam/users/*',
  '/iam/groups/*',
  '/iam/policies/*',
  '/iam/grants/*'
] as const

export type DisabledRoutePattern = (typeof DISABLED_ROUTE_PATTERNS)[number]

export const TEMPLATE_FIELD_KEYS = [
  'name',
  'description',
  'model',
  'filesystem',
  'todoList',
  'askUser',
  'readBeforeWrite',
  'webSearch',
  'currentTime',
  'compression',
  'faultTolerance',
  'toolPermissions',
  'channels',
  'plugins',
] as const

export type TemplateField = (typeof TEMPLATE_FIELD_KEYS)[number]

export type CapabilityUnavailableBehavior =
  'hidden' | 'disabled' | 'tooltip' | 'readonly' | 'redirect'

export type CapabilityPolicy = {
  enabled: boolean
  unavailableBehavior?: CapabilityUnavailableBehavior
  message?: string
  redirectTo?: string
}

export type CapabilitySetting = boolean | CapabilityPolicy

export const DASHBOARD_OVERRIDE_ENTRIES = ['private-001'] as const

export type DashboardOverrideEntry = (typeof DASHBOARD_OVERRIDE_ENTRIES)[number]

export type DashboardConfig =
  | {
      mode: 'agent_operations'
      showQuickCreateTemplate: CapabilitySetting
      showQuickCreateInstance: CapabilitySetting
    }
  | {
      /** Replaces the entire dashboard content area for a private deployment. */
      mode: 'replace'
      entry: DashboardOverrideEntry
    }

export type DeploymentLogoUrl =
  | string
  | {
      light: string
      dark: string
      collapsed?: string
    }

export type DeploymentProfile = {
  version: 1
  profileId: string
  branding: {
    productName: string
    logoUrl?: DeploymentLogoUrl
  }
  login: {
    mode: LoginMode
    /** Private SAML deployments redirect directly to this tenant's IdP entry. */
    samlDomain?: string
  }
  dashboard: DashboardConfig
  navigation: {
    sidebar: {
      /** Omit to keep all menus; provide an array to enable allow-list mode. */
      visibleItems?: SidebarMenuKey[]
      defaultItem?: SidebarMenuKey
    }
    routes: {
      /** Route patterns that are unavailable in this deployment. */
      disabledPatterns?: DisabledRoutePattern[]
    }
  }
  agent: {
    templates: {
      visible: CapabilitySetting
      create: CapabilitySetting
      restart: CapabilitySetting
      delete: CapabilitySetting
      /**
       * Template form field overrides. Fields omitted from this map remain editable.
       */
      fields: Partial<Record<TemplateField, CapabilitySetting>>
    }
    instances: {
      visible: CapabilitySetting
      create: CapabilitySetting
      edit: CapabilitySetting
      delete: CapabilitySetting
    }
  }
  channels: {
    visible: CapabilitySetting
    allowedTypes: string[]
    accountCreation: CapabilitySetting
  }
  credentials: {
    modelProvider: CapabilitySetting
    objectStorage: CapabilitySetting
    agentApiToken: CapabilitySetting
    websocketToken: CapabilitySetting
  }
  modules: {
    sandbox: CapabilitySetting
    mcp: CapabilitySetting
    memory: CapabilitySetting
    knowledgeBase: CapabilitySetting
    skillGroups: CapabilitySetting
    skillManagement: CapabilitySetting
    skillMarketplace: CapabilitySetting
  }
  iam: {
    users: {
      visible: CapabilitySetting
      create: CapabilitySetting
      edit: CapabilitySetting
      delete: CapabilitySetting
      grant: CapabilitySetting
      groupAssign: CapabilitySetting
    }
    groups: CapabilitySetting
    policies: CapabilitySetting
    grants: CapabilitySetting
    ssoConfig: CapabilitySetting
  }
  profile: {
    visible: CapabilitySetting
  }
  theme?: {
    primaryColor?: string
    density?: 'compact' | 'comfortable'
  }
}

export type DeploymentCapability = string
